
import { Component, Prop, Vue } from 'vue-property-decorator';
import loading from '@/components/loading';
import ThemeButton from '@/components/ThemeButton.vue';
import * as Locale from '@/plugins/i18n';
import { TranslateResult } from 'vue-i18n';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { analytics, BTN_EVTS } from '@/common/logs';

enum Route {
  LIVE_CHAT_OVERLOADED = 'LIVE_CHAT_OVERLOADED',
  VOICE_CHAT_OVERLOADED = 'VOICE_CHAT_OVERLOADED',
  BOTH_LIVE_VOICE_CHAT_OVERLOADED = 'BOTH_LIVE_VOICE_CHAT_OVERLOADED'
}

const btn = (
  type: 'outlined' | 'primary' | 'text',
  text: string | TranslateResult,
  click: (e: MouseEvent) => void,
  description?: string | TranslateResult,
) => ({ id: `${type}-${text}`, text, type, description, click });

const OVERLOADED_CHANNEL_MODAL_EVENTS = {
  back: 'back',
  openRequest: 'openRequest',
  liveChat: 'liveChat',
  call: 'call',
} as const;

type ThresholdLimitModalEvent = typeof OVERLOADED_CHANNEL_MODAL_EVENTS[keyof typeof OVERLOADED_CHANNEL_MODAL_EVENTS];

const MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP = {
  [OVERLOADED_CHANNEL_MODAL_EVENTS.back]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_BACK,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.openRequest]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_OPEN_REQUEST,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.liveChat]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_LIVE_CHAT,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.call]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_CALL,
} as const;

@Component({
  directives: { loading },
  components: { ThemeButton },
  mixins: [LogPageEventMixin],
})
export default class ThresholdLimitModal extends Vue {
  @Prop() readonly liveChatWaitTimeDescription?: string | TranslateResult;
  @Prop() readonly callWaitTimeDescription?: string | TranslateResult;
  @Prop() readonly hasLiveChatOnlineAgents?: boolean;
  @Prop() readonly hasCallOnlineAgents?: boolean;

  loading = false;
  route: Route = Route.LIVE_CHAT_OVERLOADED;

  created () {
    if (!this.hasLiveChatOnlineAgents && !this.hasCallOnlineAgents) {
      this.route = Route.BOTH_LIVE_VOICE_CHAT_OVERLOADED;
    } else if (!this.hasCallOnlineAgents) {
      this.route = Route.VOICE_CHAT_OVERLOADED;
    } else {
      this.route = Route.LIVE_CHAT_OVERLOADED;
    }
  }

  get [PAGE_TRACKING] () {
    return {
      name: PAGE_EVENTS.MODAL_THRESHOLD_LIMIT,
      metadata: {
        hasLiveChatOnlineAgents: this.hasLiveChatOnlineAgents,
        hasCallOnlineAgents: this.hasCallOnlineAgents,
      },
    };
  }

  get modal () {
    const createEventHandler = (event: ThresholdLimitModalEvent) => () => {
      const modalEvent = MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP[event];

      if (modalEvent) {
        analytics.trackClick({
          name: modalEvent,
        });
      }

      this.$emit(event);
    };

    const openRequestBtn = btn(
      'primary',
      Locale.i18n.t('page.connect.open_request.text'),
      createEventHandler('openRequest')
    );

    const backButton = btn(
      'text',
      Locale.i18n.t('page.connect.overloaded.cancel'),
      createEventHandler('back')
    );

    const RouteModal = {
      [Route.LIVE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t('page.connect.overloaded.live_chat_modal.title'),
        message: {
          text: Locale.i18n.t('page.connect.overloaded.live_chat_modal.message'),
        },
        buttons: [
          openRequestBtn,
          btn(
            'primary',
            Locale.i18n.t('page.connect.call'),
            createEventHandler('call'),
            this.callWaitTimeDescription
          ),
          backButton,
        ],
      }),
      [Route.VOICE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t('page.connect.overloaded.voice_chat_modal.title'),
        message: {
          text: Locale.i18n.t(
            'page.connect.overloaded.voice_chat_modal.message'
          ),
        },
        buttons: [
          openRequestBtn,
          btn(
            'primary',
            Locale.i18n.t('page.connect.live_chat'),
            createEventHandler('liveChat'),
            this.liveChatWaitTimeDescription
          ),
          backButton,
        ],
      }),
      [Route.BOTH_LIVE_VOICE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t(
          'page.connect.overloaded.both_live_voice_chat_modal.title'
        ),
        message: {
          html: true,
          text: `${Locale.i18n.t(
            'page.connect.overloaded.both_live_voice_chat_modal.message'
          )}</br></br>${Locale.i18n.t(
            'page.connect.overloaded.both_live_voice_chat_modal.bottom_message'
          )}`,
        },
        buttons: [
          openRequestBtn,
          backButton,
        ],
      }),
    };

    return RouteModal[this.route]?.();
  }
}
